import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { IncidentNote } from '../../types/canvision.types';

interface IncidentNotesProps {
  notes?: IncidentNote[];
  onAddNote: (note: string) => Promise<void>;
}

// Format date/time for display
function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  let datePrefix: string;
  if (date.toDateString() === today.toDateString()) {
    datePrefix = 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    datePrefix = 'Yesterday';
  } else {
    datePrefix = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${datePrefix} ${time}`;
}

function IncidentNotes({ notes = [], onAddNote }: IncidentNotesProps) {
  const [showInput, setShowInput] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!noteText.trim()) return;

    try {
      setSubmitting(true);
      setError(null);
      await onAddNote(noteText.trim());
      setNoteText('');
      setShowInput(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNoteText('');
    setShowInput(false);
    setError(null);
  };

  // Sort notes by created_at descending (newest first)
  const sortedNotes = [...notes].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <Box>
      {/* Add Note Button/Input */}
      {!showInput ? (
        <Button
          startIcon={<AddIcon />}
          onClick={() => setShowInput(true)}
          size="small"
          sx={{ mb: 1 }}
        >
          Add Note
        </Button>
      ) : (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder="Add a note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            disabled={submitting}
            autoFocus
          />
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {error}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !noteText.trim()}
              startIcon={submitting ? <CircularProgress size={14} /> : null}
            >
              {submitting ? 'Adding...' : 'Add'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Notes List */}
      {sortedNotes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
          No notes yet
        </Typography>
      ) : (
        <List dense disablePadding>
          {sortedNotes.map((note) => (
            <ListItem
              key={note.id}
              sx={{
                px: 0,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <ListItemText
                primary={note.note}
                secondary={
                  <Box component="span" sx={{ display: 'flex', gap: 1 }}>
                    <Typography component="span" variant="caption" fontWeight="medium">
                      {note.user_name || 'Unknown'}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary">
                      {formatDateTime(note.created_at)}
                    </Typography>
                  </Box>
                }
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { whiteSpace: 'pre-wrap' },
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

export default IncidentNotes;
